/**
 * SAB-989 — commissioner-configurable positional roster caps ("max 2 QB").
 *
 * Observed during the League of Trash draft: nothing stops a team drafting five QBs onto
 * bench slots. Slot eligibility says WHERE a player may sit; it says nothing about HOW MANY
 * of a position a roster may hold, because bench slots take anyone.
 *
 * ── WHERE THE CAPS LIVE ─────────────────────────────────────────────────────
 *
 * `nfl_leagues.settings.position_limits`, beside `roster_slots`. Shape:
 *
 *     { "QB": 2, "RB": 6, "TE": 2 }
 *
 * A position ABSENT from the map is uncapped. That is the default and it is deliberate:
 * blank means uncapped per the ticket, so an existing league with no `position_limits` key
 * behaves exactly as it does today. There is no "0 means unlimited" convention — 0 is a
 * real cap meaning "none of this position", which a commissioner might genuinely want.
 *
 * ── WHY THIS IS A SHARED MODULE ─────────────────────────────────────────────
 *
 * Three places must agree or the draft wedges, which is the SAB-967 failure class the
 * ticket names explicitly:
 *
 *   1. the draft room, to grey the button with a reason;
 *   2. nfl_acquire_pick_lock, which is the authority and REFUSES;
 *   3. the autopick engine and queue, which must SKIP a capped-out player.
 *
 * If (3) disagrees with (2), autopick proposes a player the RPC then refuses, the caller
 * breaks its advance loop on a failed pick, and the draft stalls for everyone -- the exact
 * shape already documented in _shared/nfl-draft-queue.ts for stale queue rows.
 */

export type PositionLimits = Record<string, number>;

/**
 * Read caps off the raw settings value.
 *
 * `unknown` on purpose: this arrives from a jsonb column via `select('*')` and nothing
 * upstream validates it. A malformed value must degrade to "uncapped", never throw on a
 * render path and never invent a cap that would refuse a legal pick.
 */
export function leaguePositionLimits(raw: unknown): PositionLimits {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return {};
  const out: PositionLimits = {};
  for (const [pos, val] of Object.entries(raw as Record<string, unknown>)) {
    // Accept ONLY a real number, or a non-empty numeric string. Everything else is dropped.
    //
    // The `Number(val)` shortcut is a trap here and my own test caught it: Number(null),
    // Number(''), Number(false) and Number([]) are all **0**, not NaN. Under a `>= 0`
    // guard that turns a null in settings into a cap of ZERO -- silently banning the
    // position league-wide, which is the exact failure this parser exists to avoid.
    let n: number;
    if (typeof val === 'number') n = val;
    else if (typeof val === 'string' && val.trim() !== '') n = Number(val);
    else continue;
    // Reject NaN, negatives and fractions.
    if (Number.isInteger(n) && n >= 0) out[pos.trim().toUpperCase()] = n;
  }
  return out;
}

/** The cap for a position, or null when uncapped. */
export function limitFor(limits: PositionLimits, position: string | null | undefined): number | null {
  if (!position) return null;
  const v = limits[position.trim().toUpperCase()];
  return v === undefined ? null : v;
}

export interface CapCheck {
  /** True when adding one more of this position would exceed the cap. */
  blocked: boolean;
  limit: number | null;
  current: number;
  /** User-facing, already phrased; null when not blocked. */
  reason: string | null;
}

/**
 * Would adding one more player of `position` break this team's cap?
 *
 * `rosterPositions` is the team's CURRENT active roster positions -- one entry per rostered
 * player, including bench and IR. IR is counted deliberately: a cap is about how many of a
 * position you may CONTROL, and parking one on IR to draft another would be the obvious way
 * around it.
 */
export function wouldExceedPositionLimit(
  limits: PositionLimits,
  rosterPositions: readonly (string | null | undefined)[],
  position: string | null | undefined,
): CapCheck {
  const limit = limitFor(limits, position);
  if (limit === null) return { blocked: false, limit: null, current: 0, reason: null };

  const want = (position ?? '').trim().toUpperCase();
  const current = rosterPositions.reduce<number>(
    (n, p) => n + ((p ?? '').trim().toUpperCase() === want ? 1 : 0), 0);

  if (current < limit) return { blocked: false, limit, current, reason: null };

  return {
    blocked: true,
    limit,
    current,
    reason: limit === 0
      ? `${want} is not allowed in this league.`
      : `Roster limit reached — max ${limit} ${want}${limit === 1 ? '' : 's'}.`,
  };
}

/**
 * SAB-989 follow-up — which positions the Position Limits grid should offer an input for.
 *
 * The grid rendered all 20 non-alias positions in the eligibility table, so a league with
 * no IDP slot still showed CB/DB/DE/DL/DT/FS/LB/MLB/NT/OLB/SAF caps. Capping a position the
 * league cannot roster is not a setting, it is noise.
 *
 * `startable` is the SAME set the players-page chips scope by (SAB-995/998), passed in
 * rather than derived here so this stays pure and there is exactly one opinion in the app
 * about what a league can hold.
 *
 * Two contracts inherited from that module, both deliberate:
 *
 *   - `startable === null` means the eligibility read has not landed OR failed, and the
 *     answer is DO NOT FILTER. Briefly showing extra inputs costs a commissioner nothing;
 *     wrongly hiding one leaves them hunting for a cap they cannot set.
 *   - filtering a non-empty list down to nothing means the league has no startable slots at
 *     all. That is not a state to render as an empty card, so the unscoped list wins.
 *
 * FB is dropped unconditionally: it presents as RB in the product (SAB-998) and carries the
 * identical slot bases, so a separate FB input offers a cap for something a commissioner
 * reads as an RB.
 */
export function visiblePositionLimits(
  orderedPositions: readonly string[],
  startable: ReadonlySet<string> | null,
): string[] {
  const visible = orderedPositions.filter((p) => p !== 'FB');
  if (startable === null) return visible;
  const scoped = visible.filter((p) => startable.has(p));
  return scoped.length > 0 ? scoped : visible;
}
